import { initializeApp } from "firebase/app";
import { getAuth, signInWithPopup, signOut, GoogleAuthProvider } from "firebase/auth";

import { toast } from "sonner";
import { track } from "./track";

const firebaseConfig = {
  apiKey: "AIzaSyAmcuGgNOX3W69AsHLpMUIXPn00rTz6tVs",
  authDomain: "auth.zatoplan.com",
  projectId: "level-scheme-446715-e3",
};

// Initialize Firebase (default app used by getAuth below)
initializeApp(firebaseConfig);
// Initialize Firebase Auth provider
const provider = new GoogleAuthProvider();

// whenever a user interacts with the provider, we force them to select an account
provider.setCustomParameters({
  prompt: "select_account ",
});

export const auth = getAuth();

// Set after the first sign-in this browser has seen (behaviour-logging FRS FR15).
const SIGNED_IN_BEFORE_KEY = "zatoplan_has_signed_in_before";

/**
 * A sign-in failure the modal can word. `code` is one of `popup_blocked`,
 * `popup_closed`, `network`, `in_app_browser` or `error` — never Firebase's own
 * codes, so the modal does not have to know Firebase.
 */
export class SignInError extends Error {
  constructor(code) {
    super(code);
    this.name = "SignInError";
    this.code = code;
  }
}

const IN_APP_BROWSERS = [
  "linkedinapp",
  "fban", // Facebook App
  "fbav", // Facebook App
  "instagram",
  "line",
  "wv", // WebView
  "fb_iab", // Facebook in-app browser
];

const FIREBASE_CODES = {
  "auth/popup-blocked": "popup_blocked",
  "auth/popup-closed-by-user": "popup_closed",
  "auth/cancelled-popup-request": "popup_closed",
  "auth/network-request-failed": "network",
};

/**
 * Google's popup does not work inside in-app browsers, so hand the page to the
 * system browser instead. The pending action cannot cross browsers: the user
 * lands on the same URL there and presses the control again.
 */
const handOffToSystemBrowser = (userAgent) => {
  const url = window.location.href;
  if (/iphone|ipad/i.test(userAgent)) {
    window.location.href = "x-safari-" + url;
    return;
  }
  if (/android/i.test(userAgent)) {
    window.location.href =
      "intent://" +
      url.replace(/^https?:\/\//, "") +
      "#Intent;scheme=https;package=com.android.chrome;end";
  }
};

/**
 * Google sign-in through Firebase's popup. Resolves with the user, rejects
 * with a `SignInError`.
 *
 * It does not navigate. It used to send everyone to `/app` with a full page
 * load, which threw away whatever the user was doing — an address in the URL,
 * a pending save — and is why every caller now decides for itself what
 * happens next (the sign-in modal resumes the action; the marketing header
 * goes to `/app`).
 *
 * `featureId` is the registry feature the sign-in was for, if any, and only
 * labels the `sign_in` event: the single place that event is emitted.
 */
export const signInWithGoogle = async ({ featureId } = {}) => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  const isInAppBrowser = IN_APP_BROWSERS.some((app) => userAgent.includes(app));
  if (/iphone|ipad|android/i.test(userAgent) && isInAppBrowser) {
    handOffToSystemBrowser(userAgent);
    throw new SignInError("in_app_browser");
  }

  let result;
  try {
    result = await signInWithPopup(auth, provider);
  } catch (error) {
    // Only the mapped code leaves here: the error object can carry auth state.
    throw new SignInError(FIREBASE_CODES[error?.code] ?? "error");
  }

  // Profile fields only, for the avatar while auth restores on a reload. The
  // ID token is never stored or logged.
  localStorage.setItem("photoURL", result.user.photoURL ?? "");
  localStorage.setItem("displayName", result.user.displayName ?? "");
  localStorage.setItem("email", result.user.email ?? "");
  toast.success("Signed in");
  // The single place sign_in / sign_up is emitted (necessary tier). "First
  // ever" is a per-browser flag, so a second device or cleared storage reads
  // as sign_up again — an accepted heuristic with no users table (FRS FR15).
  let returning = true;
  try {
    returning = localStorage.getItem(SIGNED_IN_BEFORE_KEY) === "1";
  } catch {
    // storage unavailable: report a sign-in rather than invent a sign-up
  }
  track(returning ? "sign_in" : "sign_up", { method: "google", feature_id: featureId ?? null });
  try {
    localStorage.setItem(SIGNED_IN_BEFORE_KEY, "1");
  } catch {
    // storage unavailable
  }
  return result.user;
};

export const handleGoogleSignOut = () => {
  // Written by builds before the auth context existed and read by nothing now;
  // cleared so a stale token does not sit in storage indefinitely.
  localStorage.removeItem("accessToken");
  signOut(auth)
    .then(() => {
      // Sign-out successful.
      // Update your UI to reflect the sign-out state.
      window.location.href = "/";
    })
    .catch((error) => {
      // An error happened. Only the code: the error object can carry auth state.
      console.error("Sign-out error:", error?.code);
    });
};
